import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Loader2, Mail, X } from "lucide-react";
import { isValidSlug, slugify } from "@rec/shared";
import { api } from "@/lib/api";
import { Badge, Button, Card, EmptyState, ErrorState, Skeleton } from "@/components/ui";
import { useServerText } from "@/i18n";
import { useFormat } from "@/i18n/format";

/**
 * Back-office: review access requests.
 *
 * Approving is the only way a workspace comes into existence — there is no
 * self-serve sign-up — so this screen provisions a tenant and sends the first
 * invitation. The API re-checks the permission; mounting under /admin is
 * organisation, not security.
 */
export function AdminAccess() {
  const { t } = useTranslation(["admin", "common"]);
  const st = useServerText();
  const f = useFormat();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"pending" | "approved" | "rejected">("pending");
  const [approving, setApproving] = useState<string | null>(null);
  const [orgName, setOrgName] = useState("");
  const [slug, setSlug] = useState("");
  /** Once an operator edits the address by hand, stop overwriting it. */
  const [slugEdited, setSlugEdited] = useState(false);

  const requests = useQuery({
    queryKey: ["access-requests", status],
    queryFn: () => api.listAccessRequests(status),
  });

  const approve = useMutation({
    mutationFn: (id: string) =>
      api.approveAccessRequest(id, { organizationName: orgName, slug }),
    onSuccess: () => {
      setApproving(null);
      setOrgName("");
      setSlug("");
      setSlugEdited(false);
      void queryClient.invalidateQueries({ queryKey: ["access-requests"] });
    },
  });

  const reject = useMutation({
    mutationFn: (id: string) => api.rejectAccessRequest(id),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["access-requests"] }),
  });

  return (
    <div className="mx-auto max-w-[760px] space-y-5">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-text">
          {t("access.title")}
        </h1>
        <p className="mt-1 text-[13.5px] text-text-muted">
          {t("access.intro")}
        </p>
      </div>

      <div className="flex gap-1">
        {(["pending", "approved", "rejected"] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setStatus(option)}
            className={
              status === option
                ? "rounded-lg bg-bg-subtle px-3 py-1.5 text-[13px] font-medium text-text capitalize"
                : "rounded-lg px-3 py-1.5 text-[13px] text-text-muted capitalize transition-colors hover:text-text"
            }
          >
            {t(`access.status.${option}`)}
          </button>
        ))}
      </div>

      {/* Error before empty — a 403 or a down API rendered as "no pending
          requests" reads as good news, and it is the opposite. That exact
          confusion happened: no operator existed, every admin call 403'd, and
          this tab alone said everything was quiet. */}
      {requests.isError ? (
        <ErrorState
          title={t("access.loadError")}
          error={requests.error}
          onRetry={() => void requests.refetch()}
        />
      ) : requests.isLoading ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : !requests.data || requests.data.length === 0 ? (
        <EmptyState
          title={t(`access.emptyTitle.${status}`)}
          description={t(`access.emptyDescription.${status}`)}
        />
      ) : (
        <div className="space-y-2">
          {requests.data.map((request) => (
            <Card key={request.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-medium text-text">
                      {request.name}
                    </span>
                    <Badge>{request.companyName}</Badge>
                  </div>
                  <span className="mt-1 flex items-center gap-1.5 text-[12.5px] text-text-muted">
                    <Mail size={12} />
                    {request.email}
                  </span>
                  {request.message ? (
                    <p className="mt-2.5 rounded-lg bg-bg-subtle px-3 py-2 text-[13px] leading-relaxed text-text-muted">
                      {request.message}
                    </p>
                  ) : null}
                  <span className="mt-2 block text-[11.5px] text-text-faint">
                    {f.dateTime(request.createdAt, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>

                {status === "pending" ? (
                  <div className="flex shrink-0 gap-1.5">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setApproving(request.id);
                        setOrgName(request.companyName);
                        // A sensible default the reviewer can correct — the API
                        // rejects anything that is not lowercase-and-hyphens.
                        setSlug(
                          request.companyName
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")
                            .replace(/^-+|-+$/g, "")
                            .slice(0, 60),
                        );
                      }}
                    >
                      <Check size={14} /> {t("access.approve")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={reject.isPending}
                      onClick={() => reject.mutate(request.id)}
                    >
                      <X size={14} /> {t("access.reject")}
                    </Button>
                  </div>
                ) : null}
              </div>

              {approving === request.id ? (
                <div className="mt-4 space-y-3 rounded-lg border border-border bg-bg-subtle p-3.5">
                  <div className="flex items-center gap-2 text-[13px] font-medium text-text">
                    <Building2 size={14} className="text-text-faint" />
                    {t("access.newWorkspace")}
                  </div>

                  <label className="block">
                    <span className="mb-1 block text-[12.5px] text-text-muted">
                      {t("access.name")}
                    </span>
                    <input
                      value={orgName}
                      onChange={(e) => {
                        setOrgName(e.target.value);
                        // The address is derived until somebody overrides it:
                        // it has to be a legal hostname label, and typing one
                        // by hand is a chore that fails at approval time.
                        if (!slugEdited) setSlug(slugify(e.target.value));
                      }}
                      className="h-9 w-full rounded-lg border border-border bg-bg px-2.5 text-[13px] text-text focus:border-accent focus:outline-none"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-[12.5px] text-text-muted">
                      {t("access.address")}
                    </span>
                    <input
                      value={slug}
                      onChange={(e) => {
                        setSlugEdited(true);
                        setSlug(e.target.value.toLowerCase());
                      }}
                      aria-describedby={`slug-help-${request.id}`}
                      className="h-9 w-full rounded-lg border border-border bg-bg px-2.5 font-mono text-[13px] text-text focus:border-accent focus:outline-none"
                    />
                    <span
                      id={`slug-help-${request.id}`}
                      className="mt-1 block text-[11.5px] text-text-faint"
                    >
                      {slug && !isValidSlug(slug)
                        ? t("access.addressInvalid")
                        : t("access.addressHelp")}
                    </span>
                  </label>

                  {approve.error ? (
                    <p
                      role="alert"
                      className="rounded-lg bg-danger-subtle px-2.5 py-1.5 text-[12.5px] text-danger"
                    >
                      {approve.error instanceof Error
                        ? st(approve.error.message)
                        : t("access.approveFailed")}
                    </p>
                  ) : null}

                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!orgName || !isValidSlug(slug) || approve.isPending}
                      onClick={() => approve.mutate(request.id)}
                    >
                      {approve.isPending ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : null}
                      {t("access.createAndInvite")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setApproving(null)}
                    >
                      {t("common:actions.cancel")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
